
import { useState, useEffect } from 'react';
import { useToast } from '@/hooks/use-toast';
import { getPlatformAdmins } from '@/services/platformAdminsService';
import { supabase } from '@/integrations/supabase/client';
import { User } from '@/types/user';

export function usePlatformAdminsData() {
  const { toast } = useToast();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchUsers = async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
        console.log('🔄 Manual refresh triggered for platform admins');
      }
      
      const platformAdmins = await getPlatformAdmins();
      console.log('Platform admins from database:', platformAdmins);
      
      setUsers(platformAdmins);
      
      if (isRefresh) {
        toast({
          title: "Success",
          description: `Refreshed successfully. Found ${platformAdmins.length} platform admins.`,
        });
      }
    } catch (error) {
      console.error('Error fetching platform admins:', error);
      toast({
        title: "Error",
        description: "Failed to fetch platform admins.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchUsers();
    
    // Set up real-time subscription for platform_admins table
    console.log('🔗 Setting up real-time subscription for platform_admins...');
    const channel = supabase
      .channel('platform-admins-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'platform_admins'
        },
        (payload) => {
          console.log('🔴 Real-time update received for platform_admins:', payload);
          fetchUsers(); // Refresh data when changes occur
        }
      )
      .subscribe();

    return () => {
      console.log('🔌 Cleaning up real-time subscription for platform_admins');
      supabase.removeChannel(channel);
    };
  }, []);

  const handleUserInvited = () => {
    console.log('🎉 Platform admin invited, refreshing list...');
    fetchUsers();
  };

  const handleUserUpdated = () => {
    console.log('✏️ Platform admin updated, refreshing list...');
    fetchUsers();
  };

  const handleManualRefresh = () => {
    fetchUsers(true);
  };

  return {
    users,
    loading,
    refreshing,
    fetchUsers,
    handleUserInvited,
    handleUserUpdated,
    handleManualRefresh
  };
}
